import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { mentionPropsForSemanticMention } from '#pplx/frontend/composer-sdk/internal/mention/mentionVariant.js';
import { $clearActiveCommandMenuQuery, $getActiveCommandMenuQuery, } from '#pplx/frontend/composer-sdk/internal/typeahead/commandMenuQuery.js';
import { $caretAfter, $deleteTriggerSpan, $insertTriggerToken, $replaceTriggerQuery, } from '#pplx/frontend/composer-sdk/internal/typeahead/editorActions.js';
import { $createMentionNode } from '#pplx/frontend/composer-sdk/lexical/nodes/MentionNodeSupport.js';
import { $createTextNode, $getNodeByKey, $getSelection, $isElementNode, $isRangeSelection, $isTextNode, } from 'lexical';
function getTokenMentionProps(spec) {
    switch (spec.kind) {
        case 'mention': {
            const { id: uuid, kind: _kind, ...props } = spec;
            return { ...props, uuid };
        }
        case 'source':
        case 'device':
        case 'space':
        case 'skill':
            return mentionPropsForSemanticMention(spec);
        case 'command':
            return {
                uuid: spec.id,
                variant: 'slash-command',
                text: spec.text,
                queryText: spec.queryText,
                slashCommandSubmittedLabel: spec.submittedLabel,
                icon: spec.icon,
            };
        default:
            return assertUnreachableValue(spec);
    }
}
// Capture in the selection's active update. Normalization can merge the split trigger
// into a sibling before a deferred mutation runs, so retain its parent-relative text
// range instead of the split node's key. Node references never cross updates.
export function makeEditorApi(editor, trigger) {
    const capturedSpan = trigger === null
        ? null
        : {
            parentKey: trigger.getParentOrThrow().getKey(),
            offset: trigger
                .getPreviousSiblings()
                .reduce((offset, sibling) => offset + sibling.getTextContentSize(), 0),
            text: trigger.getTextContent(),
        };
    const withTriggerSpan = ({ mutate, discrete, }) => {
        editor.update(() => {
            if (capturedSpan === null) {
                return;
            }
            const parent = $getNodeByKey(capturedSpan.parentKey);
            if (!$isElementNode(parent) || !parent.isAttached()) {
                return;
            }
            let start = capturedSpan.offset;
            for (const node of parent.getChildren()) {
                const size = node.getTextContentSize();
                if (start >= size) {
                    start -= size;
                    continue;
                }
                const end = start + capturedSpan.text.length;
                // A rewritten draft must not let a stale selection replace unrelated text.
                if ($isTextNode(node) && node.getTextContent().slice(start, end) === capturedSpan.text) {
                    mutate({ node, start, end });
                }
                return;
            }
        }, { discrete });
    };
    return {
        insertToken: (spec, opts) => withTriggerSpan({
            mutate: (span) => $insertTriggerToken({
                ...span,
                props: getTokenMentionProps(spec),
                trailingSpace: opts?.trailingSpace ?? true,
            }),
        }),
        replaceActiveTriggerQuery: (text) => withTriggerSpan({
            mutate: (span) => $replaceTriggerQuery({ ...span, text }),
        }),
        deleteActiveTrigger: () => withTriggerSpan({
            // Navigation can unmount the editor as soon as the selection handler returns.
            discrete: true,
            mutate: $deleteTriggerSpan,
        }),
    };
}
/**
 * Build the editor handle for a marker-anchored command-menu selection. The
 * "active trigger" is the marker's query span, so every mutation first clears
 * the query and the marker (closing the menu), then applies at the caret.
 *
 * The query span is captured at creation: `closeMenu()` removes the marker
 * right after `onSelect` returns, so a handler that defers its mutation would
 * otherwise find nothing to resolve. A deferred mutation replays against the
 * captured span, deleting exactly the captured query; when its nodes are gone
 * or the text no longer matches (draft rewritten), it degrades to a no-op like
 * the typed-trigger handle does.
 */
export function makeMarkerEditorApi(editor) {
    let capturedSpan = null;
    editor.getEditorState().read(() => {
        const activeQuery = $getActiveCommandMenuQuery();
        if (activeQuery) {
            capturedSpan = {
                textNodeKey: activeQuery.textNode?.getKey() ?? null,
                parentKey: activeQuery.parentNode.getKey(),
                markerOffset: activeQuery.markerOffset,
                queryStartOffset: activeQuery.marker.getQueryStartOffset(),
                query: activeQuery.query,
            };
        }
    });
    // Clear the live query, or replay the captured span when the marker is
    // already gone; returns whether a caret was established for the insert.
    const $clearQueryOrReplayCapturedSpan = () => {
        if ($clearActiveCommandMenuQuery()) {
            return true;
        }
        if (!capturedSpan) {
            return false;
        }
        const textNode = capturedSpan.textNodeKey ? $getNodeByKey(capturedSpan.textNodeKey) : null;
        if ($isTextNode(textNode)) {
            const { queryStartOffset, query } = capturedSpan;
            // By replay time the node can hold more than the stale query — text typed
            // after the close, or a sibling merged in by the marker's removal — so
            // truncating past the offset would delete it along with the query.
            const liveQuery = textNode
                .getTextContent()
                .slice(queryStartOffset, queryStartOffset + query.length);
            if (liveQuery !== query) {
                return false;
            }
            textNode.spliceText(queryStartOffset, query.length, '');
            textNode.select(queryStartOffset, queryStartOffset);
            return true;
        }
        const parentNode = $getNodeByKey(capturedSpan.parentKey);
        if (!$isElementNode(parentNode)) {
            return false;
        }
        const offset = Math.min(capturedSpan.markerOffset, parentNode.getChildrenSize());
        parentNode.select(offset, offset);
        return true;
    };
    const withClearedQuery = ({ insert, discrete, }) => {
        editor.update(() => {
            if (!$clearQueryOrReplayCapturedSpan()) {
                return;
            }
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                insert(selection);
            }
        }, { discrete });
    };
    return {
        insertToken: (spec, opts) => withClearedQuery({
            insert: (selection) => {
                const mention = $createMentionNode(getTokenMentionProps(spec));
                const trailing = (opts?.trailingSpace ?? true) ? $createTextNode(' ') : null;
                selection.insertNodes(trailing ? [mention, trailing] : [mention]);
                // Same post-insert fix-up as `$insertTriggerToken`: an isolated mention
                // leaves `insertNodes`'s caret placement undefined without it.
                $caretAfter(mention, trailing);
            },
        }),
        replaceActiveTriggerQuery: (text) => withClearedQuery({
            insert: (selection) => {
                selection.insertText(text);
            },
        }),
        deleteActiveTrigger: () => withClearedQuery({ insert: () => { }, discrete: true }),
    };
}
//# sourceMappingURL=editorApi.js.map